import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow, format } from 'date-fns';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCalendar, FiUser, FiMessageSquare, FiClock, FiSend } = FiIcons;

const TaskDetails = ({ task, onClose }) => {
  const { addComment, addTimeLog, teamMembers } = useData();
  const { user } = useAuth();
  const [comment, setComment] = useState('');
  const [timeEntry, setTimeEntry] = useState({ hours: '', description: '' });
  const [activeTab, setActiveTab] = useState('comments');

  const assignee = teamMembers.find(m => m.id === task.assignedTo);
  const assigner = teamMembers.find(m => m.id === task.assignedBy);

  const handleAddComment = (e) => {
    e.preventDefault();
    if (comment.trim()) {
      addComment(task.id, comment.trim());
      setComment('');
    }
  };

  const handleAddTimeLog = (e) => {
    e.preventDefault();
    if (timeEntry.hours && timeEntry.description.trim()) {
      addTimeLog(task.id, {
        hours: parseFloat(timeEntry.hours),
        description: timeEntry.description.trim()
      });
      setTimeEntry({ hours: '', description: '' });
    }
  };

  const totalHours = task.timeLog?.reduce((sum, log) => sum + log.hours, 0) || 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{task.title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-80px)]">
          {/* Left Panel - Task Details */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600">{task.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Assigned To</h4>
                  <div className="flex items-center space-x-2">
                    <img
                      src={assignee?.avatar}
                      alt={assignee?.name}
                      className="w-8 h-8 rounded-full"
                    />
                    <span className="text-gray-600">{assignee?.name || 'Unassigned'}</span>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Created By</h4>
                  <div className="flex items-center space-x-2">
                    <img
                      src={assigner?.avatar}
                      alt={assigner?.name}
                      className="w-8 h-8 rounded-full"
                    />
                    <span className="text-gray-600">{assigner?.name || 'Unknown'}</span>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Priority</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                    task.priority === 'high' ? 'bg-red-100 text-red-800' :
                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {task.priority}
                  </span>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Deadline</h4>
                  <div className="flex items-center space-x-1 text-gray-600">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    <span>{format(new Date(task.deadline), 'MMM dd, yyyy')}</span>
                  </div>
                </div>
              </div>

              {totalHours > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Time Tracking</h4>
                  <div className="flex items-center space-x-1 text-gray-600">
                    <SafeIcon icon={FiClock} className="w-4 h-4" />
                    <span>{totalHours} hours logged</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Comments & Time Logs */}
          <div className="w-96 border-l border-gray-200 flex flex-col">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('comments')}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  activeTab === 'comments'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Comments ({task.comments?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('time')}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  activeTab === 'time'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Time Logs ({task.timeLog?.length || 0})
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {activeTab === 'comments' ? (
                <div className="space-y-4">
                  {task.comments?.map(comment => {
                    const commentUser = teamMembers.find(m => m.id === comment.userId);
                    return (
                      <div key={comment.id} className="flex space-x-3">
                        <img
                          src={commentUser?.avatar}
                          alt={commentUser?.name}
                          className="w-8 h-8 rounded-full flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {commentUser?.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{comment.text}</p>
                        </div>
                      </div>
                    );
                  })}

                  {(!task.comments || task.comments.length === 0) && (
                    <p className="text-gray-500 text-sm text-center py-8">
                      No comments yet
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {task.timeLog?.map(log => {
                    const logUser = teamMembers.find(m => m.id === log.userId);
                    return (
                      <div key={log.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <img
                              src={logUser?.avatar}
                              alt={logUser?.name}
                              className="w-6 h-6 rounded-full"
                            />
                            <span className="text-sm font-medium text-gray-900">
                              {logUser?.name}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-blue-600">
                            {log.hours}h
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{log.description}</p>
                        <span className="text-xs text-gray-500">
                          {format(new Date(log.date), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    );
                  })}

                  {(!task.timeLog || task.timeLog.length === 0) && (
                    <p className="text-gray-500 text-sm text-center py-8">
                      No time logs yet
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Input Forms */}
            <div className="border-t border-gray-200 p-4">
              {activeTab === 'comments' ? (
                <form onSubmit={handleAddComment} className="space-y-3">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  <button
                    type="submit"
                    disabled={!comment.trim()}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                    <span>Send</span>
                  </button>
                </form>
              ) : (
                <form onSubmit={handleAddTimeLog} className="space-y-3">
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={timeEntry.hours}
                    onChange={(e) => setTimeEntry({ ...timeEntry, hours: e.target.value })}
                    placeholder="Hours worked"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <textarea
                    value={timeEntry.description}
                    onChange={(e) => setTimeEntry({ ...timeEntry, description: e.target.value })}
                    placeholder="What did you work on?"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  <button
                    type="submit"
                    disabled={!timeEntry.hours || !timeEntry.description.trim()}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SafeIcon icon={FiClock} className="w-4 h-4" />
                    <span>Log Time</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TaskDetails;